import groupBy from 'lodash/groupBy';
import { ValueGetterParams, GridApi } from 'ag-grid-community';

import { valueExists } from './utils';
import { ReportColDef } from './types';
import { BASE_PREFIX, COMPARE_PREFIX, VARIANCE_PREFIX, LABEL_COLUMN_ID } from './constants';
import { DefaultValueRenderer } from './columnRenderers';
import { NoDetailValue } from '../scenarioEditDetails/constants';

import { alignLeftCellStyle } from '@client-shared/ui-agGrid';

const TOTAL_COLUMN_ID = 'TOTAL';

const BASE_TOTAL_COLUMN_ID = `${BASE_PREFIX}${TOTAL_COLUMN_ID}`;
const COMPARE_TOTAL_COLUMN_ID = `${COMPARE_PREFIX}${TOTAL_COLUMN_ID}`;
const VARIANCE_TOTAL_COLUMN_ID = `${VARIANCE_PREFIX}${TOTAL_COLUMN_ID}`;

const getNumberValue = (params: ValueGetterParams, colId: string) => {
  const value = params.getValue(colId);
  if (!value || value === NoDetailValue) {
    return 0;
  }
  return value;
};

const sumAllGetter = (params: ValueGetterParams, prefix: string): number =>
  params?.api?.getColumns()?.reduce((acc, column) => {
    if (
      (column.getUserProvidedColDef() as ReportColDef)?.isIncludedInTotalColumn
      && column.getColId()?.startsWith(prefix)
    ) {
      return acc + getNumberValue(params, column.getColId());
    }
    return acc;
  }, 0) ?? 0;


const sumPerCompanyCodeGetter = (params: ValueGetterParams, prefix: string): number => {
  const colDefs = params?.api?.getColumns()
    ?.filter(column => (column.getUserProvidedColDef() as ReportColDef)?.isIncludedInTotalColumn
      && column.getColId().startsWith(prefix)
      && params.getValue(column.getColId())
    )
    .map(column => column.getColDef());

  const colDefsGroupedByCompanyCode = groupBy(colDefs, 'companyCode');

  return Object.values(colDefsGroupedByCompanyCode).reduce(
    (acc, colDefs) => {
      // @ts-ignore
      const columnWithValue = colDefs.find(colDef => colDef.colId && getNumberValue(params, colDef.colId));
      if (!columnWithValue) {
        return acc;
      }
      // @ts-ignore
      return acc + getNumberValue(params, columnWithValue!.colId!);
    },
    0);
};

export const CALCULATE_TOTAL_TYPES = {
  SUM_ALL: 'SUM_ALL',
  SUM_PER_COMPANY_CODE: 'SUM_PER_COMPANY_CODE',
} as const;

export const CALCULATE_TOTAL_STRATEGY = {
  [CALCULATE_TOTAL_TYPES.SUM_ALL]: sumAllGetter,
  [CALCULATE_TOTAL_TYPES.SUM_PER_COMPANY_CODE]: sumPerCompanyCodeGetter,
};

export const totalColumnGetter = (params: ValueGetterParams, prefix='') => {
  const calculateTotal = params.data.calculateTotal as keyof typeof CALCULATE_TOTAL_STRATEGY;

  if (calculateTotal in CALCULATE_TOTAL_STRATEGY) {
    return CALCULATE_TOTAL_STRATEGY[calculateTotal](params, prefix);
  }

  return null;
};

export const totalColumnDefinitionFactory = ({
  headerName,
  field,
  colId,
  valueGetter = totalColumnGetter,
  customAlignment = {},
}: {
  headerName: string,
  field: string,
  colId: string,
  valueGetter?: (params: ValueGetterParams, prefix?: string) => any,
  customAlignment?: { cellStyle: { 'text-align': string } } | {},
}) => ({
  headerName,
  field,
  colId,
  width: 150,
  minWidth: 150,
  valueGetter,
  cellRenderer: DefaultValueRenderer,
  cellClassRules: {
    'cell-variance': () => true,
  },
  ...customAlignment,
});

export const getTotalColumn = () =>
  totalColumnDefinitionFactory({
    headerName: 'Total',
    field: TOTAL_COLUMN_ID,
    colId: TOTAL_COLUMN_ID,
  });

export const getBaseCompareVarianceTotalColumn = () => ({
  headerName: 'Total',
  children: [
    totalColumnDefinitionFactory({
      headerName: '1st scenario',
      field: BASE_TOTAL_COLUMN_ID,
      colId: BASE_TOTAL_COLUMN_ID,
      valueGetter: params => totalColumnGetter(params, BASE_PREFIX),
    }),
    totalColumnDefinitionFactory({
      headerName: '2nd scenario',
      field: COMPARE_TOTAL_COLUMN_ID,
      colId: COMPARE_TOTAL_COLUMN_ID,
      valueGetter: params => totalColumnGetter(params, COMPARE_PREFIX),
    }),
    totalColumnDefinitionFactory({
      headerName: 'Variance',
      field: VARIANCE_TOTAL_COLUMN_ID,
      colId: VARIANCE_TOTAL_COLUMN_ID,
      valueGetter: params => {
        const baseTotal = params.getValue(BASE_TOTAL_COLUMN_ID);
        const compareTotal = params.getValue(COMPARE_TOTAL_COLUMN_ID);

        if (valueExists(baseTotal) && valueExists(compareTotal)) {
          return baseTotal - compareTotal;
        }

        return null;
      },
      customAlignment: alignLeftCellStyle,
    }),
  ],
});

export const retrieveDataFromTotalColumn = (gridApi: GridApi, isComparing = false) => {
  const totalColumn: {[key: string]: { base: any, compare?: any, variance?: any}} = {};

  gridApi.forEachNode(node => {
    const label = gridApi.getValue(LABEL_COLUMN_ID, node) as string;
    if (label) {
      if (isComparing) {
        totalColumn[label] = {
          base: gridApi.getValue(BASE_TOTAL_COLUMN_ID, node),
          compare: gridApi.getValue(COMPARE_TOTAL_COLUMN_ID, node),
          variance: gridApi.getValue(VARIANCE_TOTAL_COLUMN_ID, node),
        };
      } else {
        totalColumn[label] = { base: gridApi.getValue(TOTAL_COLUMN_ID, node) };
      }
    }
  });

  return totalColumn;
};
